import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Eye, Trash2, Check, Ban, ShieldCheck } from "lucide-react";
import { Button } from "../../../shared/ui";
import {
  getAdminUser,
  patchAdminUser,
  deleteAdminUser,
  impersonateUser,
  type AdminUserDetail as Detail,
} from "../../../features/admin/api/adminApi";
import { startImpersonation } from "../../../features/admin/impersonation";
import { PageHeader, StatusPill, shortDate, money } from "./_shared";
import WorkspaceBillingEditor from "./WorkspaceBillingEditor";

export default function AdminUserDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => id && getAdminUser(id).then(setDetail).catch(() => undefined);
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [id]);

  if (!detail) {
    return (
      <div className="flex h-full items-center justify-center text-ink-400">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  const u = detail.user;
  const isOwner = u.role === "saas_owner";

  const setStatus = async (accountStatus: "approved" | "banned") => {
    setBusy(true);
    try {
      await patchAdminUser(u._id, { accountStatus });
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed");
    } finally {
      setBusy(false);
    }
  };

  const setRole = async (role: "saas_owner" | "saas_user") => {
    if (!confirm(role === "saas_owner" ? `Promote ${u.email} to SUPER ADMIN?` : `Demote ${u.email} to a regular user?`)) return;
    setBusy(true);
    try {
      await patchAdminUser(u._id, { role });
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed");
    } finally {
      setBusy(false);
    }
  };

  const doImpersonate = async () => {
    if (!confirm(`Log in as ${u.email}? You'll be able to return to admin.`)) return;
    try {
      const { token, user } = await impersonateUser(u._id);
      startImpersonation(token, { name: user.name, email: user.email });
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed");
    }
  };

  const doDelete = async () => {
    if (!confirm(`Permanently delete ${u.email}? This cannot be undone.`)) return;
    try {
      await deleteAdminUser(u._id);
      navigate("/admin/users");
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed");
    }
  };

  return (
    <>
      <PageHeader
        title={u.name}
        subtitle={u.email}
        actions={
          <button onClick={() => navigate("/admin/users")} className="flex items-center gap-1.5 rounded-lg border border-ink-200 px-3 py-1.5 text-[12.5px] font-medium text-ink-600 hover:border-gold-300">
            <ArrowLeft size={14} /> All users
          </button>
        }
      />
      <div className="grid grid-cols-1 gap-5 p-4 sm:p-6 lg:grid-cols-[320px_1fr]">
        {/* Profile + actions */}
        <div className="space-y-4">
          <div className="surface-card p-5">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-ink-900 text-[16px] font-semibold text-white">
                {u.name.slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="truncate font-semibold text-ink-900">{u.name}</p>
                <p className="truncate text-[12px] text-ink-400">{u.email}</p>
              </div>
            </div>
            <dl className="mt-4 space-y-2 text-[12.5px]">
              <Row label="Status"><StatusPill status={u.accountStatus} /></Row>
              <Row label="Role">{isOwner ? "Super admin" : "User"}</Row>
              {u.companyName && <Row label="Company">{u.companyName}</Row>}
              <Row label="Last login">{shortDate(u.lastLoginAt)}</Row>
              <Row label="Joined">{shortDate(u.createdAt)}</Row>
            </dl>
          </div>

          <div className="surface-card space-y-2 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-400">Actions</p>
            {u.accountStatus !== "approved" && (
              <Button variant="primary" size="sm" className="w-full justify-center" disabled={busy} onClick={() => setStatus("approved")}>
                <Check size={14} /> Approve account
              </Button>
            )}
            {u.accountStatus !== "banned" && !isOwner && (
              <Button variant="danger" size="sm" className="w-full justify-center" disabled={busy} onClick={() => setStatus("banned")}>
                <Ban size={14} /> Ban account
              </Button>
            )}
            {!isOwner && (
              <Button variant="secondary" size="sm" className="w-full justify-center" disabled={busy} onClick={doImpersonate}>
                <Eye size={14} /> Impersonate
              </Button>
            )}
            {!isOwner ? (
              <Button variant="ghost" size="sm" className="w-full justify-center" disabled={busy} onClick={() => setRole("saas_owner")}>
                <ShieldCheck size={14} /> Make super admin
              </Button>
            ) : (
              <Button variant="ghost" size="sm" className="w-full justify-center" disabled={busy} onClick={() => setRole("saas_user")}>
                <ShieldCheck size={14} /> Revoke super admin
              </Button>
            )}
            {!isOwner && (
              <Button variant="ghost" size="sm" className="w-full justify-center text-rose" disabled={busy} onClick={doDelete}>
                <Trash2 size={14} /> Delete user
              </Button>
            )}
          </div>
        </div>

        {/* Workspaces */}
        <div className="space-y-3">
          <h3 className="text-[13px] font-semibold text-ink-900">Workspaces ({detail.workspaces.length})</h3>
          {detail.workspaces.length === 0 && <p className="text-[12.5px] text-ink-400">No workspaces.</p>}
          {detail.workspaces.map((ws) => (
            <div key={ws.id} className="surface-card p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-semibold text-ink-900">
                    {ws.name} {ws.isOwner && <span className="ml-1 text-[10.5px] font-medium text-ink-400">owner</span>}
                  </p>
                  <p className="text-[11.5px] text-ink-400">/{ws.slug} · {ws.modelCount} models · {ws.memberCount} members</p>
                </div>
                <div className="flex items-center gap-2">
                  <StatusPill status={ws.billingType} />
                  <span className="text-[12px] text-ink-600">
                    {ws.plan ? `${ws.plan.name} · ${money(ws.plan.monthlyPrice)}/mo` : "No plan"}
                    {ws.planLapsed && <span className="ml-1 text-rose">(lapsed)</span>}
                  </span>
                </div>
              </div>
              <div className="mt-3">
                <WorkspaceBillingEditor
                  workspaceId={ws.id}
                  billingType={ws.billingType}
                  planId={ws.plan?.id ?? null}
                  manualPlanEndsAt={ws.manualPlanEndsAt}
                  onSaved={load}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-ink-400">{label}</dt>
      <dd className="text-ink-800">{children}</dd>
    </div>
  );
}
